import { Component, ChangeDetectorRef } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/session.service";
import { DashboardService } from "../../dashboard.service";
import { GenericWidgetComponent } from "../generic-widget/generic-widget.component";
import { Widget } from "../widgets";
import { PermissionService } from "src/app/permission.service";
import {
  FEATURE_AURA_KEY,
  FEATURE_EXPLORIST_KEY,
  FEATURE_TRIPIST_KEY,
} from "src/app/features.constants";
import { IntegrationsService } from "src/app/integrations/integrations.service";
import { take } from "rxjs/operators";
import { forkJoin } from "rxjs";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

@Component({
  selector: "app-assistant-devices-users-widget",
  templateUrl: "./assistant-devices-users-widget.component.html",
  styleUrls: ["./assistant-devices-users-widget.component.scss"],
})
export class AssistantDevicesUsersWidgetComponent extends GenericWidgetComponent {
  widget!: Widget;
  devicesUsersChartOptions: any;
  totalDevices: any;
  totalUsers: any;
  devicesUsersData: any;
  enabledIntegrationTypes: Array<'tripist' | 'explorist' | 'aura'> = [];
  primaryIntegrationType: 'tripist' | 'explorist' | 'aura' | null = null;
  integrationIds: number[] = [];
  noIntegrationFound: boolean = false;
  bucket: "day" | "week" | "month" | "quarter" | "year" = "day";

  constructor(
    private DashboardService: DashboardService,
    private SessionService: SessionService,
    private PermissionService: PermissionService,
    private IntegrationsService: IntegrationsService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  get accountID() {
    return this.SessionService.currentAccountValue!.accountID;
  }

  get accountAppID() {
    return this.SessionService.currentAppValue!.id;
  }

  async ngOnInit(): Promise<void> {
    await this.determineIntegrationType();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // Don't call initData here - wait for integration detection to complete
    // this.initData();

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        if (
          this.filterForm.valid &&
          this.filterForm.controls.startDate.value <
            this.filterForm.controls.endDate.value
        ) {
          this.initData();
        }
      });
  }

  private async determineIntegrationType(): Promise<void> {
    const enabledTypes: Array<'tripist' | 'explorist' | 'aura'> = [];
    if (this.PermissionService.hasFeature(FEATURE_TRIPIST_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('tripist');
    }
    if (this.PermissionService.hasFeature(FEATURE_EXPLORIST_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('explorist');
    }
    if (this.PermissionService.hasFeature(FEATURE_AURA_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('aura');
    }

    this.enabledIntegrationTypes = enabledTypes;

    const integrations = await this.getIntegrations();
    const providerByType: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'tripist_assistant',
      explorist: 'explorist_assistant',
      aura: 'aura_assistant',
    };
    const preferredType = this.widget?.widgetDTO?.assistantType;
    const orderedTypes = (
      preferredType && enabledTypes.includes(preferredType)
        ? [preferredType, ...enabledTypes.filter((type) => type !== preferredType)]
        : enabledTypes
    ) as Array<'tripist' | 'explorist' | 'aura'>;

    let selectedType: 'tripist' | 'explorist' | 'aura' | null = null;
    let selectedId: number | null = null;
    for (const type of orderedTypes) {
      const integration = integrations.find((item: any) => item.provider?.type === providerByType[type]);
      if (integration?.id) {
        selectedType = type;
        selectedId = integration.id;
        break;
      }
    }

    this.primaryIntegrationType = selectedType ?? ((preferredType as any) || null);
    this.integrationIds = selectedId ? [selectedId] : [];
    this.noIntegrationFound = !selectedId;
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Now that integration detection is complete, initialize data
    this.initData();
  }

  private async getIntegrations(): Promise<any[]> {
    try {
      const accountId = this.SessionService.currentAccountValue?.accountID;
      const appId = this.SessionService.currentAppValue?.id;
      
      if (!accountId || !appId) {
        console.warn('Missing session data for integration lookup');
        return [];
      }
      
      const response: any = await this.IntegrationsService.getIntegrations(
        accountId,
        appId,
        true
      ).pipe(take(1)).toPromise();
      
      return Array.isArray(response) ? response : response?.records || [];
    } catch (error) {
      console.error(`Error fetching integrations for assistant widgets:`, error);
      return [];
    }
  }

  get widgetTitle(): string {
    return `${this.primaryAssistantLabel} Devices/Users`;
  }

  get enabledAssistantLabel(): string {
    const labels: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'Tripist',
      explorist: 'Explorist',
      aura: 'Aura',
    };
    return this.enabledIntegrationTypes.map((type) => labels[type]).join(', ') || 'Assistant';
  }

  get primaryAssistantLabel(): string {
    const labels: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'Tripist',
      explorist: 'Explorist',
      aura: 'Aura',
    };
    return this.primaryIntegrationType ? labels[this.primaryIntegrationType] : 'Assistant';
  }

  initData() {
    if (this.noIntegrationFound) {
      console.warn('No integration found for Assistant Devices/Users widget');
      return;
    }

    if (this.integrationIds.length === 0) {
      console.warn('No integration IDs available for Assistant Devices/Users widget');
      return;
    }

    this.filterForm.controls.endDate.value.setHours(23, 59, 59);

    const requests = this.integrationIds.map((integrationId) =>
      this.DashboardService.getAssistantDevicesUsers(
        this.accountID,
        this.accountAppID,
        integrationId,
        this.filterForm.controls.startDate.value,
        this.filterForm.controls.endDate.value,
      )
    );

    this.elementLoader
      .waitForEvent(forkJoin(requests))
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((responses) => {
        this.devicesUsersData = this.mergeDevicesUsersResponses(responses as any[]);
        this.bucket = this.devicesUsersData.bucket || "day";
        
        if (this.devicesUsersData.data && this.devicesUsersData.data.length > 0) {
          // Calculate the totals by summing the daily values
          this.totalDevices = this.devicesUsersData.data.reduce((sum: number, day: any) => 
            sum + parseInt(day.daily_devices_created), 0);
          this.totalUsers = this.devicesUsersData.data.reduce((sum: number, day: any) => 
            sum + parseInt(day.daily_users_created), 0);
        } else {
          this.totalDevices = 0;
          this.totalUsers = 0;
        }

        this.initDevicesUsersChart();
      });
  }

  private mergeDevicesUsersResponses(responses: any[]): any {
    const byDate = new Map<string, any>();
    const bucket = (responses && responses[0] && responses[0].bucket) || "day";

    for (const response of responses || []) {
      for (const row of response?.data || []) {
        const dateKey = row.date;
        if (!byDate.has(dateKey)) {
          byDate.set(dateKey, {
            date: dateKey,
            daily_devices_created: 0,
            daily_users_created: 0,
          });
        }

        const target = byDate.get(dateKey);
        target.daily_devices_created += Number(row.daily_devices_created || 0);
        target.daily_users_created += Number(row.daily_users_created || 0);
      }
    }

    const data = Array.from(byDate.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    return { data, bucket };
  }

  initDevicesUsersChart() {
    // Check if we have the expected data structure
    if (!this.devicesUsersData || !this.devicesUsersData.data || !Array.isArray(this.devicesUsersData.data)) {
      console.warn('Assistant Devices/Users Widget - Missing expected data structure:', this.devicesUsersData);
      return;
    }
    
    const type = this.bucket;

    const devicesUsersDataObj: {
      type: "day" | "week" | "month" | "quarter" | "year";
      data: {
        date: Date;
        devicesCreated: number;
        usersCreated: number;
      }[];
    } = { type, data: [] };

    if (type === "day" || type === "week" || type === "month" || type === "quarter" || type === "year") {
      for (let i = 0; i < this.devicesUsersData.data.length; i++) {
        const dayData = this.devicesUsersData.data[i];
        
        devicesUsersDataObj.data.push({
          date: new Date(dayData.date),
          devicesCreated: parseInt(dayData.daily_devices_created),
          usersCreated: parseInt(dayData.daily_users_created),
        });
      }
    }

    this.devicesUsersChartOptions = {
      grid: {
        left: 30,
        top: 0,
        right: 30,
        bottom: 30,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: devicesUsersDataObj.data.map((data: any) => data.date.toISOString()),
        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            return this.getXLabel(d, type as any);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisPointer: {
          type: 'shadow'
        }
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          const dateValue = params[0].axisValue;
          const dateString = typeof dateValue === 'string' ? dateValue : dateValue.toISOString();
          return this.getTooltip(params, type as any, dateString);
        },
      },
      series: [
        {
          name: 'Devices Created',
          data: devicesUsersDataObj.data.map((data: any) => data.devicesCreated),
          showSymbol: false,
          label: {
            color: "#7dc4fd",
          },
          type: "bar",
          itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
          large: true,
          barMaxWidth: "40%",
        },
        {
          name: 'Users Created',
          data: devicesUsersDataObj.data.map((data: any) => data.usersCreated),
          showSymbol: false,
          type: "line",
          itemStyle: { color: "black" },
          lineStyle: { color: "black" },
        }
      ],
    };
  }

  private getQuarter(date: Date): number {
    return Math.floor(date.getMonth() / 3) + 1;
  }

  private getXLabel(dateString: string, type: "day" | "week" | "month" | "quarter" | "year"): string {
    const date = new Date(dateString);
    if (type === "day") {
      return this.getDayLabel(date);
    } else if (type === "week") {
      return this.getWeekLabel(date);
    } else if (type === "month") {
      return this.getMonthLabel(date);
    } else if (type === "quarter") {
      return `Q${this.getQuarter(date)} ${date.getFullYear()}`;
    } else if (type === "year") {
      return `${date.getFullYear()}`;
    }
    return "";
  }

  private getDayLabel(date: Date) {
    // Check if we have more than 30 days of data
    if (this.devicesUsersData && this.devicesUsersData.data && this.devicesUsersData.data.length > 30) {
      // Show every other day for better readability
      const day = date.getDate();
      return day % 2 === 0 ? "" : day.toString();
    }
    return date.getDate().toString();
  }

  private getWeekLabel(date: Date) {
    const currentDate = date.getDate();
    if (currentDate < 7) {
      return MONTHS[date.getMonth()];
    }
    return `${date.getMonth()}/${date.getDate()}`;
  }

  private getMonthLabel(date: Date) {
    const currentMonth = date.getMonth();
    if (currentMonth === 0) {
      return date.getFullYear().toString();
    }
    return MONTHS[currentMonth];
  }

  private getTooltip(params: any, type: "day" | "week" | "month" | "quarter" | "year", dateString: string): string {
    const date = new Date(dateString);

    if (type === "day") {
      return `
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}
      <br>
      <span style="color: black;"><strong>Devices Created: ${params[0].value}</strong></span>
      <br>
      <span style="color: black;"><strong>Users Created: ${params[1]?.value || 0}</strong></span>
      `;
    } else if (type === "week") {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 7);
      return `
      Week Of:<br>
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()} - ${
        MONTHS[endDate.getMonth()]
      } ${endDate.getDate()} ${endDate.getFullYear()}
      <br>
      <span style="color: black;"><strong>Devices Created: ${params[0].value}</strong></span>
      <br>
      <span style="color: black;"><strong>Users Created: ${params[1]?.value || 0}</strong></span>
      `;
    } else if (type === "month") {
      return `
      ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}
      <br><span style="color: black;"><strong>Devices Created: ${params[0].value}</strong></span>
      <br><span style="color: black;"><strong>Users Created: ${params[1]?.value || 0}</strong></span>
      `;
    } else if (type === "quarter") {
      return `
      Q${this.getQuarter(date)} ${date.getFullYear()}
      <br><span style="color: black;"><strong>Devices Created: ${params[0].value}</strong></span>
      <br><span style="color: black;"><strong>Users Created: ${params[1]?.value || 0}</strong></span>
      `;
    } else if (type === "year") {
      return `
      ${date.getFullYear()}
      <br><span style="color: black;"><strong>Devices Created: ${params[0].value}</strong></span>
      <br><span style="color: black;"><strong>Users Created: ${params[1]?.value || 0}</strong></span>
      `;
    }
    return "";
  }
} 